import app from 'flarum/forum/app';
import { extend } from 'flarum/common/extend';
import Button from 'flarum/common/components/Button';
import CommentPost from 'flarum/forum/components/CommentPost';

import { MEETUPS_TAG_SLUG } from './utils/routeState';

/**
 * D8 «Me interesa · N» en las propuestas de encuentro (T12B · objetivo 9).
 *
 * --- LA DECISIÓN, CON LA EVIDENCIA QUE LA SOSTIENE ---
 *
 * Se reutiliza `flarum/likes` como mecanismo y NO se construye un contador propio. Tres datos,
 * comprobados en el banco antes de escribir esto:
 *
 * 1. **Ya está instalado y habilitado**: `extensions_enabled` lo lista (`flarum-likes`), así que
 *    no añade dependencia ni paso de despliegue.
 * 2. **El doble-conteo ya está resuelto donde toca**: la tabla `post_likes` tiene la pareja
 *    (post, usuario) como clave, de modo que pulsar dos veces no suma dos. Un contador propio
 *    tendría que reimplementar ese candado — y sería el tercer sitio de este paquete donde se
 *    escribe la misma protección.
 * 3. Trae de regalo lo que nadie pidió pero se agradece: aviso al autor de la propuesta y la
 *    lista de quién ha mostrado interés.
 *
 * Lo que le falta a `likes` —y es lo que aporta este módulo— es el CONTEXTO: dice «Me gusta» sin
 * número y sin umbral. Una propuesta de encuentro necesita las tres cosas juntas para cerrar el
 * bucle de M6: cuánta gente, cuánta hace falta, y qué pasa al llegar.
 *
 * --- DÓNDE, Y POR QUÉ SÓLO AHÍ ---
 *
 * Sólo en el PRIMER post de un hilo de la categoría Encuentros. El primer post ES la propuesta;
 * en las respuestas «me interesa» no significa nada y el «me gusta» normal sigue sirviendo.
 *
 * Cuando se pinta, el botón nativo de «me gusta» se retira de ese post: dejar los dos sería el
 * mismo control dos veces con nombres distintos, sumando al mismo contador — exactamente el
 * «copy redundante» que estos paquetes vienen a quitar.
 */

/** Umbral por defecto si el ajuste no está puesto. Espeja `MeetupOptions::INTEREST_THRESHOLD`. */
const FALLBACK_THRESHOLD = 15;

function threshold(): number {
  const limits: any = app.forum.attribute('umbralComunidadMeetupLimits');
  const value = limits && Number(limits.interestThreshold);

  return value && value > 0 ? value : FALLBACK_THRESHOLD;
}

/** ¿Es este post la propuesta —el primero— de un hilo de Encuentros? */
function isProposal(post: any): boolean {
  if (!post || typeof post.number !== 'function' || post.number() !== 1) return false;

  const discussion = post.discussion && post.discussion();

  if (!discussion || typeof discussion.tags !== 'function') return false;

  const tags = discussion.tags();

  // `tags()` devuelve `null` mientras la relación no está cargada (y `false` si no existe). Sin
  // esta guarda, un hilo abierto en frío revienta el render del post entero.
  if (!Array.isArray(tags)) return false;

  return tags.some((tag: any) => tag && typeof tag.slug === 'function' && tag.slug() === MEETUPS_TAG_SLUG);
}

export default function addInterestButton() {
  extend(CommentPost.prototype, 'actionItems', function (this: any, items: any) {
    const post = this.attrs.post;

    if (!isProposal(post)) return;

    // El botón nativo, fuera: mismo contador, dos nombres. `has()` antes de `remove()` porque
    // `flarum/likes` no lo añade cuando el post no se puede votar (cerrado, oculto, sin
    // permiso), y ahí no hay nada que quitar.
    if (items.has('like')) items.remove('like');

    const likes = post.likes && post.likes();
    const count = Array.isArray(likes) ? likes.length : 0;
    const goal = threshold();
    const user = app.session.user;
    const interested = !!(user && Array.isArray(likes) && likes.some((u: any) => u === user));

    // Sin sesión el contador SÍ se ve (es señal social, la lección X1 del Spec) pero el botón
    // no hace nada: se pinta como estado, no como control que engañe.
    const canVote = !!user && typeof post.canLike === 'function' && post.canLike();

    items.add(
      'umbralInterest',
      <div className="CmInterest" data-cm-interest={count} data-cm-goal={goal} data-cm-mine={interested ? '1' : '0'}>
        <Button
          className={'Button CmInterest-btn' + (interested ? ' CmInterest-btn--on' : '')}
          data-cm-interest-btn="1"
          disabled={!canVote}
          onclick={() => toggle(post, interested)}
        >
          {app.translator.trans('umbral-comunidad.forum.interest.' + (interested ? 'on' : 'off'), { count })}
        </Button>

        <span className="CmInterest-goal">
          {count >= goal
            ? app.translator.trans('umbral-comunidad.forum.interest.reached', { goal })
            : app.translator.trans('umbral-comunidad.forum.interest.remaining', { goal, missing: goal - count })}
        </span>
      </div>,
      10
    );
  });
}

/**
 * Alterna el interés.
 *
 * Copia deliberada de lo que hace `flarum/likes` en su `addLikeAction`: guarda `isLiked` y
 * ADEMÁS toca a mano `data.relationships.likes.data`, porque el servidor no devuelve la
 * relación actualizada y sin ese empujón el contador no se mueve hasta recargar. Se replica en
 * vez de llamar a su función porque su `onclick` es una closure dentro de un `extend`, sin
 * export al que agarrarse.
 */
function toggle(post: any, interested: boolean) {
  const user = app.session.user;

  if (!user) return;

  post.save({ isLiked: !interested });

  const data = post.data?.relationships?.likes?.data;

  if (!Array.isArray(data)) return;

  const index = data.findIndex((like: any) => like.id === user.id());

  if (index >= 0) data.splice(index, 1);

  if (!interested) data.unshift({ type: 'users', id: user.id() });
}
